"use client"
import { Fragment, useEffect } from 'react';
import { UserProfileHero } from './UserProfileHero';
import { ScreenLoader } from '../common/loaders/ScreenLoader';
import { useSession, useUser } from '@clerk/nextjs';
import { useSelector } from 'react-redux';
import { Navbar, NavbarActions } from '../common/navbar';
import { PageMenu } from './PageMenu';
import { FormattedMessage, useIntl } from 'react-intl';
import ExtensionLists from '../extensions/list';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { isProfileComplete } from '@/lib/auth/isProfileComplete';
import { Profile } from './profile';
import ElementLists from './element_builder';
import T3headlessLists from './t3headless';
import T3themesLists from './t3themes';
import MembershipList from './membership';
import PackageList from '../packagebuilder/list';


const User = ({ slug, baseurl }: any) => {
    const { user, isLoaded: userLoaded } = useUser();
    const { session, isLoaded: sessionLoaded } = useSession();
    const router = useRouter();
    const { membership, permission, membershiperror, permissionerror, membershipLoading, permissionLoading } = useSelector((state: any) => state.customer);
    const intl = useIntl();
    const image = (
        <img
            src={user?.hasImage ? user?.imageUrl ?? "" : '/assets/media/avatars/blank.png'}
            className="rounded-full border-3 border-success h-[100px] w-[100px] object-contain"
        />

    );

    useEffect(() => {
        if (!userLoaded || !sessionLoaded) return;
        if (!user?.id) {
            toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
            router.push('/sign-in');
            return;
        }

        if (!session?.id) {
            toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
            router.push('/sign-in');
            return;
        }

        if (!isProfileComplete(user)) {
            toast.error(intl.formatMessage({ id: 'PROFILE.INCOMPLETE_MESSAGE' }));
            router.push("/user");
            return;
        }

    }, [userLoaded, sessionLoaded, user, session, router]);

    useEffect(() => {

        if (membershiperror !== null && membershiperror !== undefined) {
            toast.error(membershiperror || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
        }

        if (permissionerror !== null && permissionerror !== undefined) {
            toast.error(permissionerror || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
        }
    }, [membershiperror, permissionerror]);

    if (!userLoaded || !sessionLoaded) {
        return <ScreenLoader />;
    }

    if (!user || !session || !userLoaded || permissionLoading || membershipLoading) {
        return <ScreenLoader />;
    }

    return (
        <Fragment>
            <UserProfileHero
                image={image}
                name={user?.fullName ?? ''}
                info={
                    [
                        user?.publicMetadata?.city && user?.publicMetadata?.country && {
                            label: `${user.publicMetadata.city}, ${user.publicMetadata.country}`,
                            icon: 'lucide-map-pin'
                        },
                        user?.emailAddresses?.[0]?.emailAddress && {
                            label: user.emailAddresses[0].emailAddress,
                            icon: 'lucide-mail'
                        }
                    ].filter(Boolean)
                }
            />
            <div className='container-fixed' >
                <Navbar>
                    <PageMenu />
                    <NavbarActions>
                        {
                            slug ?
                                slug == "extension_builder" ?
                                    <div className="text-sm text-gray-500 flex gap-2.5">
                                        <span className='text-gray-700 font-normal'><FormattedMessage id="MAX_USE" />:&nbsp;<span className='text-gray-800 font-normal'>{permission?.extension_info?.max ?? "0"}</span></span>
                                        <span className='text-gray-700 font-normal'><FormattedMessage id="TOTAL_USE" />:&nbsp;<span className='text-gray-800 font-normal'> {permission?.extension_info?.current ?? "0"}</span></span>
                                    </div>
                                    : slug == "element_builder" ?
                                        <div className="text-sm text-gray-500 flex gap-2">
                                            <span className='text-gray-700 font-normal'><FormattedMessage id="MAX_USE" />:&nbsp;<span className='text-gray-800 font-normal'>{permission?.element_info?.max ?? "0"}</span></span>
                                            <span className='text-gray-700 font-normal'><FormattedMessage id="TOTAL_USE" />:&nbsp;<span className='text-gray-800 font-normal'> {permission?.element_info?.current ?? "0"}</span></span>
                                        </div>
                                        : slug == "t3themes" ?
                                            <div className="text-sm text-gray-500 flex gap-2">
                                                <span className='text-gray-700 font-normal'><FormattedMessage id="MAX_USE" />:&nbsp;<span className='text-gray-800 font-normal'>{permission?.t3themes_config?.max ?? "0"}</span></span>
                                                <span className='text-gray-700 font-normal'><FormattedMessage id="TOTAL_USE" />:&nbsp;<span className='text-gray-800 font-normal'> {permission?.t3themes_config?.current ?? "0"}</span></span>
                                            </div>
                                            : slug == "t3headless" ?
                                                <div className="text-sm text-gray-500 flex gap-2">
                                                    <span className='text-gray-700 font-normal'><FormattedMessage id="MAX_USE" />:&nbsp;<span className='text-gray-800 font-normal'>{permission?.t3headless_config?.max ?? "0"}</span></span>
                                                    <span className='text-gray-700 font-normal'><FormattedMessage id="TOTAL_USE" />:&nbsp;<span className='text-gray-800 font-normal'> {permission?.t3headless_config?.current ?? "0"}</span></span>
                                                </div>
                                                : slug == "package_builder" ?
                                                    <div className="text-sm text-gray-500 flex gap-2">
                                                        <span className='text-gray-700 font-normal'><FormattedMessage id="MAX_USE" />:&nbsp;<span className='text-gray-800 font-normal'>{permission?.package_info?.max ?? "0"}</span></span>
                                                        <span className='text-gray-700 font-normal'><FormattedMessage id="TOTAL_USE" />:&nbsp;<span className='text-gray-800 font-normal'> {permission?.package_info?.current ?? "0"}</span></span>
                                                    </div>
                                                    : null

                                : null
                        }
                    </NavbarActions>
                </Navbar>

                {
                    slug == "extension_builder" ?
                        <ExtensionLists baseurl={baseurl} membership={true} total={permission?.extension_info?.current} />
                        : slug == "element_builder" ?
                            <ElementLists total={permission?.element_info?.current} permission={permission} membership={membership} />
                            : slug == "t3themes" ?
                                <T3themesLists total={permission?.t3themes_config?.current} permission={permission} membership={membership} />
                                : slug == "t3headless" ?
                                    <T3headlessLists total={permission?.t3headless_config?.current} permission={permission} membership={membership} />
                                    : slug == "package_builder" ?
                                        <PackageList total={permission?.package_info?.current} permission={permission} membership={true} />
                                        : slug == "membership" ?
                                            <MembershipList total={permission?.element_info?.current} permission={permission} />
                                            : <Profile membership={membership} />
                }
            </div>
        </Fragment>
    );
};

export { User };
